import { useRef } from "react";
import { ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";
import { Eye, Heart, MessageCircle, TrendingUp } from "lucide-react";

const SCROLL_STEP = 320;
const PLACEHOLDER_IMAGE = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="500" viewBox="0 0 400 500">
    <rect width="400" height="500" fill="#f3f4f6"/>
    <g fill="#9ca3af">
      <circle cx="170" cy="210" r="18"/>
      <path d="M120 290l45-52 34 39 24-28 57 66H120z"/>
    </g>
    <text x="200" y="340" text-anchor="middle" font-family="Arial, sans-serif" font-size="16" fill="#6b7280">
      Portfolio media unavailable
    </text>
  </svg>`
)}`;
const VIDEO_URL_RE = /\.(mp4|webm|ogg)(\?|$)/i;

const Portfolio = ({ items = [] }) => {
  const scrollRef = useRef(null);
  const hasItems = Array.isArray(items) && items.length > 0;

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction * SCROLL_STEP,
      behavior: "smooth",
    });
  };

  return (
    <div>
      <h2 className="md:text-2xl text-lg font-semibold mb-6">Portfolio</h2>

      <div className="rounded-xl border border-gray-200">
        {!hasItems && (
          <div className="text-center py-12">
            <ImageIcon size={40} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-sm">
              No portfolio items available yet.
            </p>
          </div>
        )}

        {hasItems && (
          <div className="relative">
            {items.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Previous portfolio items"
                  onClick={() => scroll(-1)}
                  className="absolute left-1 top-1/2 z-10 -translate-y-1/2 rounded-full border border-gray-200 bg-white/95 p-2 shadow-md transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  <ChevronLeft size={20} className="text-gray-700" />
                </button>
                <button
                  type="button"
                  aria-label="Next portfolio items"
                  onClick={() => scroll(1)}
                  className="absolute right-1 top-1/2 z-10 -translate-y-1/2 rounded-full border border-gray-200 bg-white/95 p-2 shadow-md transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  <ChevronRight size={20} className="text-gray-700" />
                </button>
              </>
            )}

            <div
              ref={scrollRef}
              className={`flex flex-nowrap gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-1 [-ms-overflow-style:none] [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-gray-100 ${
                items.length > 1 ? "px-11" : ""
              }`}
            >
              {items.map((item, i) => {
                const rawThumbnail =
                  item?.thumbnail || item?.thumbnailUrl || item?.thumb || null;
                const mediaSource =
                  item?.mediaUrl ||
                  item?.media_url ||
                  (Array.isArray(item?.mediaUrls) ? item.mediaUrls[0] : null) ||
                  null;
                // Legacy records may incorrectly put an expiring signed mp4 URL into `thumbnail`.
                // Never feed video URLs to <img>; fall back to media source or placeholder.
                const thumbnail =
                  rawThumbnail && VIDEO_URL_RE.test(String(rawThumbnail))
                    ? null
                    : rawThumbnail;
                const previewSource = thumbnail || mediaSource || PLACEHOLDER_IMAGE;
                const mediaType = String(
                  item?.mediaType || item?.type || ""
                ).toUpperCase();
                const isVideo =
                  mediaType === "VIDEO" ||
                  mediaType === "REEL" ||
                  VIDEO_URL_RE.test(String(previewSource || ""));

                return (
                  <div
                  key={i}
                  onClick={() => {
                    if (item?.permalink) {
                      window.open(item.permalink, "_blank", "noopener,noreferrer");
                    }
                  }}
                  className="relative w-[min(280px,calc(100vw-5rem))] flex-shrink-0 snap-start rounded-xl overflow-hidden group cursor-pointer"
                >
                  {isVideo && !thumbnail ? (
                    <video
                      src={previewSource}
                      className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-105"
                      muted
                      playsInline
                      preload="metadata"
                      onError={(e) => {
                        e.currentTarget.poster = PLACEHOLDER_IMAGE;
                        e.currentTarget.removeAttribute("src");
                        e.currentTarget.load();
                      }}
                    />
                  ) : (
                    <img
                      src={previewSource}
                      alt="Portfolio"
                      className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-105"
                      onError={(e) => {
                        // Prevent error loops when the original source fails.
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = PLACEHOLDER_IMAGE;
                      }}
                    />
                  )}
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition" />
                  {item?.insights && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2 space-y-1">
                      <div className="font-medium truncate">
                        {item.mediaType || "Media"}
                      </div>
                      <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-sm">
                        <span className="flex items-center gap-1"><TrendingUp size={12} className="text-blue-500" /> Reach: {item.insights.reach ?? 0}</span>
                        <span className="flex items-center gap-1"><Eye size={12} className="text-green-500" /> Views: {item.insights.views ?? 0}</span>
                        <span className="flex items-center gap-1"><Heart size={12} className="text-red-500" /> Likes: {item.insights.likes ?? 0}</span>
                        <span className="flex items-center gap-1"><MessageCircle size={12} className="text-purple-500" /> Comments: {item.insights.comments ?? 0}</span>
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Portfolio;
